import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { type DefaultValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import ModalForm from '@/components/Modal/ModalForm'
import AvailabilityDayField from '@/components/common/AvailabilityDayField'
import DirtyModal from '@/components/common/DirtyModal'
import {
  type AvailabilityForm,
  availabilityFormSchema
} from '@/constants/availability-form'
import { daysOfWeek } from '@/constants/days-of-week'
import type { TimetableSettings } from '@/modules/timetable'

import styles from './RoomAvailabilityModal.module.scss'

type RoomAvailabilityModalProps = {
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  loading?: boolean

  onSubmit: (data: AvailabilityForm) => void
  defaultValues?: DefaultValues<AvailabilityForm>

  workingHours: TimetableSettings
}

const RoomAvailabilityModal = (props: RoomAvailabilityModalProps) => {
  const form = useForm<AvailabilityForm>({
    resolver: zodResolver(availabilityFormSchema(props.workingHours)),
    mode: 'onBlur',
    defaultValues: props.defaultValues
  })

  const { t } = useTranslation('rooms')

  const [isDirtyModalOpen, setIsDirtyModalOpen] = useState(false)
  const isDirty = form.formState.isDirty

  const handleClose = () =>
    isDirty ? setIsDirtyModalOpen(true) : props.onOpenChange(false)

  return (
    <>
      <ModalForm
        form={form}
        onSubmit={props.onSubmit}
        open={props.open}
        submitText={t('button.set')}
        title={t('header.availability-of-room')}
        onCancel={handleClose}
        onClose={handleClose}
        onOpenChange={props.onOpenChange}
        size="md"
        loading={props.loading}
        contentClassName={styles.modal}
        boxClassName={styles.container}
      >
        {daysOfWeek.map(day => (
          <AvailabilityDayField
            form={form}
            key={day}
            day={day}
            appendOnSwitchOn
          />
        ))}
      </ModalForm>
      <DirtyModal
        isOpen={isDirtyModalOpen}
        onOpenChange={setIsDirtyModalOpen}
        onConfirm={() => {
          props.onOpenChange(false)
          setIsDirtyModalOpen(false)
        }}
      />
    </>
  )
}

export default RoomAvailabilityModal
